import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  Clock, 
  Database, 
  Globe, 
  MemoryStick, 
  RefreshCw, 
  Server, 
  TrendingUp,
  Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { AdvancedPerformanceAnalytics } from '@/components/AdvancedPerformanceAnalytics';

interface PerformanceMetric {
  id: string;
  metric_name: string;
  metric_value: number;
  metric_unit: string;
  tags: Record<string, any>;
  created_at: string;
}

interface PerformanceStats {
  responseTime: number;
  throughput: number;
  errorRate: number;
  memoryUsage: number;
  cpuUsage: number;
  dbConnections: number;
}

export const PerformanceOptimization = () => {
  // Legacy component - redirect to advanced analytics
  return <AdvancedPerformanceAnalytics />;
};

const LegacyPerformanceOptimization = () => {
  const { user } = useAuth();
  const [realTimeStats, setRealTimeStats] = useState<PerformanceStats>({
    responseTime: 0,
    throughput: 0,
    errorRate: 0,
    memoryUsage: 0,
    cpuUsage: 0,
    dbConnections: 0
  });

  // Fetch system metrics
  const { data: metrics, isLoading, refetch } = useQuery({
    queryKey: ['performance-metrics'],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('system_metrics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as PerformanceMetric[];
    },
    enabled: !!user?.id,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Simulate real-time performance monitoring
  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeStats({
        responseTime: Math.random() * 200 + 50, // 50-250ms
        throughput: Math.random() * 1000 + 500, // 500-1500 req/min
        errorRate: Math.random() * 5, // 0-5%
        memoryUsage: Math.random() * 40 + 40, // 40-80%
        cpuUsage: Math.random() * 60 + 20, // 20-80%
        dbConnections: Math.floor(Math.random() * 20 + 5) // 5-25 connections
      });
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Process metrics for charts
  const processMetricsForChart = (metricName: string) => {
    if (!metrics) return [];
    
    return metrics
      .filter(m => m.metric_name === metricName)
      .slice(0, 20)
      .reverse()
      .map((metric, index) => ({
        time: new Date(metric.created_at).toLocaleTimeString(),
        value: metric.metric_value,
        index
      }));
  };

  const responseTimeData = processMetricsForChart('response_time');
  const throughputData = processMetricsForChart('throughput');
  const errorRateData = processMetricsForChart('error_rate');

  const getStatusColor = (value: number, type: 'response' | 'error' | 'usage') => {
    switch (type) {
      case 'response':
        return value < 100 ? 'text-green-600' : value < 200 ? 'text-yellow-600' : 'text-red-600';
      case 'error':
        return value < 1 ? 'text-green-600' : value < 3 ? 'text-yellow-600' : 'text-red-600';
      case 'usage':
        return value < 60 ? 'text-green-600' : value < 80 ? 'text-yellow-600' : 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getPerformanceGrade = () => {
    const avgResponse = realTimeStats.responseTime;
    const errorRate = realTimeStats.errorRate;
    const memoryUsage = realTimeStats.memoryUsage;
    
    if (avgResponse < 100 && errorRate < 1 && memoryUsage < 60) return 'A';
    if (avgResponse < 150 && errorRate < 2 && memoryUsage < 70) return 'B';
    if (avgResponse < 200 && errorRate < 3 && memoryUsage < 80) return 'C';
    return 'D';
  };

  const performanceGrade = getPerformanceGrade();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Performance Monitoring</h1>
          <p className="text-muted-foreground">Real-time system performance and optimization insights</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge 
            variant={performanceGrade === 'A' ? 'default' : performanceGrade === 'B' ? 'secondary' : 'destructive'}
            className="text-lg px-3 py-1"
          >
            Grade: {performanceGrade}
          </Badge>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Real-time KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getStatusColor(realTimeStats.responseTime, 'response')}`}>
              {(realTimeStats.responseTime || 0).toFixed(1)}ms
            </div>
            <p className="text-xs text-muted-foreground">
              Average response time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Throughput</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {(realTimeStats.throughput || 0).toFixed(0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Requests per minute
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getStatusColor(realTimeStats.errorRate, 'error')}`}>
              {(realTimeStats.errorRate || 0).toFixed(2)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Error percentage
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
            <MemoryStick className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getStatusColor(realTimeStats.memoryUsage, 'usage')}`}>
              {(realTimeStats.memoryUsage || 0).toFixed(1)}%
            </div>
            <Progress value={realTimeStats.memoryUsage} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">CPU Usage</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getStatusColor(realTimeStats.cpuUsage, 'usage')}`}>
              {(realTimeStats.cpuUsage || 0).toFixed(1)}%
            </div>
            <Progress value={realTimeStats.cpuUsage} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">DB Connections</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {realTimeStats.dbConnections}
            </div>
            <p className="text-xs text-muted-foreground">
              Active connections
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="detailed">Detailed Metrics</TabsTrigger>
          <TabsTrigger value="optimization">Optimization Tips</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Response Time Trend</CardTitle>
                <CardDescription>Last 20 measurements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={responseTimeData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--primary))' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Throughput Analysis</CardTitle>
                <CardDescription>Requests per minute over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={throughputData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--primary))" 
                        fill="hsl(var(--primary) / 0.2)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="detailed" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Health Indicators</CardTitle>
              <CardDescription>Detailed performance metrics and thresholds</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">API Latency</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{(realTimeStats.responseTime || 0).toFixed(1)}ms</div>
                      <div className="text-sm text-muted-foreground">Target: &lt;100ms</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Server className="h-4 w-4 text-green-500" />
                      <span className="font-medium">Uptime</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">99.9%</div>
                      <div className="text-sm text-muted-foreground">Last 30 days</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Database className="h-4 w-4 text-purple-500" />
                      <span className="font-medium">Query Performance</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">85ms</div>
                      <div className="text-sm text-muted-foreground">Avg query time</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <MemoryStick className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium">Cache Hit Rate</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">94.5%</div>
                      <div className="text-sm text-muted-foreground">Redis cache</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="optimization" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Performance Recommendations</CardTitle>
                <CardDescription>Suggested optimizations based on current metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {realTimeStats.responseTime > 150 && (
                    <div className="p-3 border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
                      <p className="font-medium">Response Time Optimization</p>
                      <p className="text-sm text-muted-foreground">
                        Consider implementing query caching or database indexing
                      </p>
                    </div>
                  )}
                  
                  {realTimeStats.memoryUsage > 70 && (
                    <div className="p-3 border-l-4 border-red-500 bg-red-50 dark:bg-red-950/20">
                      <p className="font-medium">Memory Usage Alert</p>
                      <p className="text-sm text-muted-foreground">
                        High memory usage detected. Consider optimizing data structures
                      </p>
                    </div>
                  )}
                  
                  {realTimeStats.errorRate > 2 && (
                    <div className="p-3 border-l-4 border-red-500 bg-red-50 dark:bg-red-950/20">
                      <p className="font-medium">Error Rate Warning</p>
                      <p className="text-sm text-muted-foreground">
                        Error rate is above threshold. Check error logs for issues
                      </p>
                    </div>
                  )}
                  
                  {performanceGrade === 'A' && (
                    <div className="p-3 border-l-4 border-green-500 bg-green-50 dark:bg-green-950/20">
                      <p className="font-medium">Excellent Performance</p>
                      <p className="text-sm text-muted-foreground">
                        All metrics are within optimal ranges
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Optimization Checklist</CardTitle>
                <CardDescription>Best practices for maintaining performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
                    <div>
                      <p className="font-medium">Database Indexing</p>
                      <p className="text-sm text-muted-foreground">Ensure proper indexes on frequently queried columns</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                    <div>
                      <p className="font-medium">Query Optimization</p>
                      <p className="text-sm text-muted-foreground">Use efficient queries and avoid N+1 problems</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500 mt-2"></div>
                    <div>
                      <p className="font-medium">Caching Strategy</p>
                      <p className="text-sm text-muted-foreground">Implement Redis caching for frequently accessed data</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2"></div>
                    <div>
                      <p className="font-medium">Error Monitoring</p>
                      <p className="text-sm text-muted-foreground">Set up alerts for error rate thresholds</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};